import { cx } from 'linaria';
import React, { forwardRef } from 'react';
import styleConfigs from '../../style-configs';
import {
    AlignItemsValues,
    AppearanceValues,
    BorderValues,
    ColorValues,
    CursorValues,
    DisplayValues,
    FlexDirectionValues,
    FontSizeValues,
    FontWeightValues,
    JustifyContentValues,
    LetterSpacingValues,
    LineHeightValues,
    MaxWidthValues,
    OutlineValues,
    OverflowValues,
    PositionValues,
    RadiusValues,
    ShadowValues,
    SizeValues,
    SpacingObject,
    SpacingValues,
    TextAlignValues,
    TextOverflowValues,
    TextTransformValues,
    TimingDurationValues,
    TimingFunctionValues,
    TransitionPropertyValues,
    WhiteSpaceValues,
} from '../../style-configs/types';

export type BoxOwnProps<E extends React.ElementType = React.ElementType> = {
    as?: E;
    position?: PositionValues;
    rowGap?: SpacingValues;
    columnGap?: SpacingValues;
    alignItems?: AlignItemsValues;
    justifyContent?: JustifyContentValues;
    display?: DisplayValues;
    flexDirection?: FlexDirectionValues;
    fontSize?: FontSizeValues;
    fontWeight?: FontWeightValues;
    margin?: SpacingObject;
    padding?: SpacingObject;
    textAlign?: TextAlignValues;
    className?: string;
    textOverflow?: TextOverflowValues;
    overflow?: OverflowValues;
    maxWidth?: MaxWidthValues;
    whiteSpace?: WhiteSpaceValues;
    lineHeight?: LineHeightValues;
    textTransform?: TextTransformValues;
    letterSpacing?: LetterSpacingValues;
    color?: ColorValues;
    borderColor?: ColorValues;
    backgroundColor?: ColorValues;
    width?: SizeValues;
    height?: SizeValues;
    minWidth?: SizeValues;
    border?: BorderValues;
    borderRadius?: RadiusValues;
    transitionProperty?: TransitionPropertyValues;
    transitionDuration?: TimingDurationValues;
    transitionTimingFunction?: TimingFunctionValues;
    boxShadow?: ShadowValues;
    cursor?: CursorValues;
    outline?: OutlineValues;
    appearance?: AppearanceValues;
};

export type BoxProps<E extends React.ElementType> = BoxOwnProps<E> &
    Omit<React.ComponentProps<E>, keyof BoxOwnProps> &
    React.AllHTMLAttributes<HTMLButtonElement>;

export function Box(props: BoxOwnProps, ref: React.Ref<Element>): JSX.Element {
    const {
        as: element = 'div',
        position,
        rowGap,
        color,
        borderColor,
        border,
        borderRadius,
        backgroundColor,
        columnGap,
        alignItems,
        justifyContent,
        display,
        fontSize,
        fontWeight,
        flexDirection,
        margin,
        padding,
        textAlign,
        overflow,
        textOverflow,
        whiteSpace,
        maxWidth,
        width,
        boxShadow,
        minWidth,
        height,
        lineHeight,
        textTransform,
        letterSpacing,
        transitionDuration,
        transitionProperty,
        transitionTimingFunction,
        cursor,
        outline,
        appearance,
        className: xClassName,
        ...passThroughProps
    } = props;
    const Component = element as React.ElementType;

    const { layout, font, colors, transitions, shadows } = styleConfigs;

    const paddingClass = cx(
        layout.paddingBottomVariants[padding?.bottom],
        layout.paddingLeftVariants[padding?.left],
        layout.paddingRightVariants[padding?.right],
        layout.paddingTopVariants[padding?.top],
    );
    const marginClass = cx(
        layout.marginBottomVariants[margin?.bottom],
        layout.marginLeftVariants[margin?.left],
        layout.marginRightVariants[margin?.right],
        layout.marginTopVariants[margin?.top],
    );

    const className = cx(
        layout.positionVariants[position],
        layout.rowGapVariants[rowGap],
        layout.columnGapVariants[columnGap],
        layout.alignmentVariants[alignItems],
        layout.distributionVariants[justifyContent],
        layout.displayVariants[display],
        layout.flexDirectionVariants[flexDirection],
        layout.textAlignVariants[textAlign],
        layout.overflowVariants[overflow],
        layout.textOverflowVariants[textOverflow],
        layout.whiteSpaceVariants[whiteSpace],
        layout.maxWidthVariants[maxWidth],
        layout.minWidthVariants[minWidth],
        layout.lineHeightVariants[lineHeight],
        layout.widthVariants[width],
        layout.heightVariants[height],
        layout.borderVariants[border],
        layout.borderRadiusVariants[borderRadius],
        layout.cursorVariants[cursor],
        layout.appearanceVariants[appearance],
        layout.outlineVariants[outline],
        font.fontSizeVariants[fontSize],
        font.fontWeightVariants[fontWeight],
        font.textTransformVariants[textTransform],
        font.letterSpacingVariants[letterSpacing],
        colors.backgroundColorVariants[backgroundColor],
        colors.colorVariants[color],
        colors.colorVariants[borderColor],
        transitions.transitionDurationVariants[transitionDuration],
        transitions.transitionTimingFunctionVariants[transitionTimingFunction],
        transitions.transitionPropertyVariants[transitionProperty],
        shadows.boxShadowVariants[boxShadow],
        paddingClass,
        marginClass,
        xClassName,
    );

    return <Component ref={ref} className={className} {...passThroughProps} />;
}

const BoxNamedRef = forwardRef<HTMLElement, BoxProps<React.ElementType>>(Box);

BoxNamedRef.displayName = 'Box';

export default BoxNamedRef;
